// REACT IMPORTS
import React, { useState, useEffect } from "react";

// CONTROLS IMPORTS
import { yupResolver } from '@hookform/resolvers/yup';
// import * as yup from 'yup';
import {useYupSchema} from '../../components/util/hookYupSchema'
import { DataGrid } from '@mui/x-data-grid';
import { esES, enUS } from '@mui/x-data-grid/locales';
import { Box, TextField, Menu, MenuItem, Divider, IconButton, Grid,
    FormControl, InputLabel, Select, Typography, Button, Checkbox, FormControlLabel,
    Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';

// ICONS IMPORTS
import CloseIcon from '@mui/icons-material/Close';

// REDUX IMPORTS
import { useDispatch, useSelector } from 'react-redux';
import { save } from '../../store/thunks';
import { selectItem, showUserModal } from '../../store/slices';

// LANGUAGE IMPORTS
import { useTranslation } from 'react-i18next';

// OWNED COMPONENTS IMPORTS
import { constants } from  '../../util'



const SaveUser = ({title, action}) => {
    // TRANSLATIONS
    const [t, i18n] = useTranslation("global");


    // LOCAL VARIABLES
    
    const sch = (yup) => yup.object({
        name: yup.string().max(40),
        email: yup.string().required().email().min(6).max(40),
        roleId: yup.number(),
        isactive: yup.boolean()
    });
    const schema = useYupSchema(sch);


    const { control, register, handleSubmit, reset, setFocus, getValues, setValue, formState: { errors: errors } } = useForm({
            resolver: yupResolver(schema),
            defaultValues: {
                name: '',
                email: '',
                roleId: 2,
                isactive: true
            }
        });
    


    // LOCAL STATES
    const [scroll, setScroll] = useState('paper');
    const [readOnly, setReadOnly] = useState(false);
    
    // NEEDED HOOKS
    const dispatch = useDispatch();
    const { selectedId, selectedItem, openUserModal } = useSelector(state => state.userAdmin);
    const { language } = useSelector(state => state.configuration);

    // EFFECTS
    useEffect(() => {
        if (openUserModal){
            setReadOnly(action==='view');
            if (action!=='add' && selectedItem)
                reset(selectedItem);
        }
    }, [openUserModal, selectedItem, action, reset]);

    
    // LOCAL FUNCTIONS
    const onSubmit = (data) => {
        dispatch(
            save
            (
                {
                    userId: action==='add' ? '-1' : selectedItem?.userId?.toString(),
                    name: data.name,
                    email: data.email,
                    isactive: data.isactive.toString(),
                    roleId: data.roleId.toString()
                }
            )
        )
    }

    const handleClose = () => {
        reset({
                    name: '',
                    email: '',
                    roleId: 2,
                    isactive: true
                });
        dispatch(selectItem(null));
        dispatch(showUserModal(false));
    };


    return (
        <>
            <Dialog
                open={openUserModal}
                onClose={handleClose}
                scroll={scroll}
                aria-labelledby="scroll-dialog-title"
                aria-describedby="scroll-dialog-description"
                sx={{ '& .MuiDialog-paper': { width: '80%', maxWidth:'440px', overflow:'hidden', } }}
            >
                <DialogTitle id="scroll-dialog-title" sx={{fontSize:'16px', flex:'.1'}}>
                    <b>{title}</b>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <DialogContent dividers={scroll === 'paper'} sx={{ flex: .8, overflow: 'auto', }}>
                        <Grid container spacing={3} direction="column"> 
                            
                            <TextField id="txtName" label={t("userAdmin.name")} variant="outlined" margin="normal" autoFocus fullWidth sx={{...constants.styles.textfield}} 
                                {...register('name')}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                disabled={readOnly || selectedItem?.roleId===3}
                            />

                            <TextField id="txtEmail" label={t("userAdmin.email")} variant="outlined" margin="normal" fullWidth sx={{...constants.styles.textfield}} 
                                {...register('email')}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                disabled={readOnly || selectedItem?.roleId===3}
                            />

                            <Controller
                                name="roleId"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.roleId}>
                                        <InputLabel id="status-label">{t("userAdmin.role")}</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="status-label"
                                            label={t("userAdmin.role")}
                                            sx={{ width: '100%', marginBottom: '0px' }}
                                            disabled={readOnly || selectedItem?.roleId===3}
                                        >
                                            <MenuItem value={1}>
                                                <Typography>{ language==='es' ? 'Administrador' : 'Administrator'}</Typography>
                                            </MenuItem>
                                            <MenuItem value={2} defaultChecked>
                                                <Typography>{ language==='es' ? 'Aprobador' : 'Reviewer'}</Typography>
                                            </MenuItem>
                                            <MenuItem value={3} disabled={true}>
                                                <Typography>{ language==='es' ? 'Artista' : 'Artist'}</Typography>
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />

                            <Controller
                                name="isactive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                color="primary"
                                                disabled={readOnly || action==='add'}
                                            />
                                        }
                                        label={
                                            language === 'es'
                                                ? 'Usuario activo'
                                                : 'Active user'
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <div style={{flex:1}} />
                    </DialogContent>
                    {
                        readOnly 
                            ? null
                            : 
                                <DialogActions>  
                                    <div style={{display:'flex', width:'100%', paddingTop:'10px', paddingBottom:'10px'}}>
                                        <div style={{flex:1}}></div>
                                        <Button
                                            type="submit"
                                            variant="outlined"
                                            sx={{ ...constants.styles.mainButton, width: 'auto-fit', paddingLeft:'50px', paddingRight:'50px', marginRight:'10px' }}
                                        >
                                            {t("addAlbumGeneral.btnSave")}
                                        </Button>
                                    </div>
                                </DialogActions>
                    }
                </Box>
            </Dialog>
        </>
    );
}

export default SaveUser;